import {
  trackDemoComplete,
  trackDemoStep,
  trackFeatureUsage,
} from "@/utils/gtm";
import type { DemoStepId } from "./flows";

/**
 * Demo-guide funnel events.
 *
 * The question these exist to answer is which of two problems we have: nobody
 * discovers the value, or they discover it and still won't pay for the trial.
 * Those need different fixes, and without the middle of the funnel between
 * `demo_view` and `trial_cta_click` there is no way to tell them apart.
 *
 * Two tiers, deliberately:
 *
 *   The run's progress — a step completed, the run finished — goes out as the
 *   dedicated `demo_step` / `demo_complete` events below, because that is the
 *   series anyone actually charts and it should not have to be reassembled
 *   from a generic event's free-text detail field.
 *
 *   Everything around the run — the picker, the checklist, exits, restarts —
 *   stays on the existing GA4 feature_usage event, so it lands in the same
 *   container with user props attached and costs no new GTM tags. This matches
 *   src/onboarding/analytics.ts.
 */
export type DemoGuideAnalyticsAction =
  | "picker_shown"
  | "picker_dismissed"
  | "desk_picked"
  /**
   * A step was passed over rather than done. Deliberately NOT a demo_step:
   * that event means the step was completed, and counting skips into it would
   * make the completion rate unreadable in the one direction that flatters us.
   */
  | "step_skipped"
  | "run_exited"
  | "checklist_opened"
  | "checklist_dismissed"
  | "watchlist_created"
  | "restarted";

export function trackDemoGuide(
  action: DemoGuideAnalyticsAction,
  detail?: string
) {
  trackFeatureUsage({
    feature_name: "demo_guide",
    feature_action: action,
    ...(detail && { feature_detail: detail }),
  });
}

/**
 * The desk template drives the variant: the run it produces differs by desk
 * (different widgets, different watchlist, and a step count of three or four),
 * so two desks are not the same demo and averaging them hides which desks
 * land. Falls back only when state predates a desk being chosen, which the
 * callers already guard against.
 */
export function demoVariant(templateId: string | undefined): string {
  return templateId ?? "unknown";
}

/** One step completed, on the action that completed it. */
export function trackDemoGuideStep(params: {
  stepId: DemoStepId;
  /** Zero-based position in the run; converted to 1-based for the event. */
  stepIndex: number;
  stepTotal: number;
  templateId: string | undefined;
}) {
  trackDemoStep({
    step_id: params.stepId,
    step_index: params.stepIndex + 1,
    step_total: params.stepTotal,
    demo_variant: demoVariant(params.templateId),
  });
}

/** The run reached the end. Once per run. */
export function trackDemoGuideComplete(params: {
  templateId: string | undefined;
  stepsCompleted: number;
}) {
  trackDemoComplete({
    demo_variant: demoVariant(params.templateId),
    steps_completed: params.stepsCompleted,
  });
}
